import { Injectable, OnDestroy, signal } from '@angular/core';
import { DocumentReference, Firestore, doc, getDoc } from '@angular/fire/firestore';
import { NavigationEnd, Router } from '@angular/router';
import { NavController, ToastController } from '@ionic/angular';
import { IClubState, ITableState, MissionTypes, TableType, IValidateClubByCodePayload } from '@soposopo/sopo-models';
import { BehaviorSubject, Observable, Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ClubService } from 'src/app/core/firebase/club.service';
import { DynamicLinkService } from 'src/app/core/firebase/dynamic-link.service';
import { AuthStoreDispatcher } from 'src/app/core/state/auth/auth.dispatcher';
import { GameStoreDispatcher } from 'src/app/core/state/game/game.dispatcher';
import { GameToLoad } from 'src/app/core/state/game/game.state';
import { ProfileStateDispatcher } from 'src/app/core/state/profile/profile.dispatcher';
import { NavigateFromLinkService } from 'src/app/game/navigate-from-link.service';
import { SopoTransitionService } from 'src/app/shared/services/sopo-transition/sopo-transition.service';
import { SopoDoubleInputSettings } from 'src/app/sopo-popup/components/sopo-double-input/sopo-double-input.component';
import { SopoPopupService, ViewContainerName } from 'src/app/sopo-popup/service/sopo-popup.service';
import { Clipboard } from '@capacitor/clipboard';
import { ApiService } from 'src/app/core/backend/api.service';
import { FeatureService } from 'src/app/core/firebase/feature.service';
import { SopoAlertSettings } from 'src/app/sopo-popup/interfaces/alert';
import { SopoStringInputSettings } from 'src/app/sopo-popup/interfaces/string-input';
import { IClubData } from 'src/app/sopo-popup/interfaces/confirm';
import { LobbyMenuSelectorTypes, SocialMenuSelectorTypes } from 'src/app/shared/components/tab-selector/tab-selector.component';

@Injectable({
    providedIn: 'root'
})
export class LobbyService implements OnDestroy{
    isInHome: BehaviorSubject<boolean> = new BehaviorSubject(false);
    routerSubscription: Subscription;
    sharingInProgress = false;
    activeMenu = signal<LobbyMenuSelectorTypes>('sweeps');
    activeSocialMenu = signal<SocialMenuSelectorTypes>('videos');

    constructor(
        private sopoPopupService: SopoPopupService,
        private clubService: ClubService,
        private dynamicLinkService: DynamicLinkService,
        private navigateFromLinkService: NavigateFromLinkService,
        private gameDispatcher: GameStoreDispatcher,
        private firestore: Firestore,
        private authDispatcher: AuthStoreDispatcher,
        private navCtrl: NavController,
        private sopoTransitionService: SopoTransitionService,
        private router: Router,
        private profileDispatcher: ProfileStateDispatcher,
        private toastCtrl: ToastController,
        private apiService: ApiService,
        private featureService: FeatureService,
    ) { 
        this.routerSubscription = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map((navigationEnd: NavigationEnd) => navigationEnd.urlAfterRedirects.includes('lobby/home') && !navigationEnd.urlAfterRedirects.includes('lobby/home/sweepstakes')),
            distinctUntilChanged()
        ).subscribe(isInHome => this.setIsInHome(isInHome));

        this.activeSocialMenu.set('videos');
        if(!this.featureService.isDesktop) {
            this.activeMenu.set('clubs');
        } else if(!this.featureService.sweepbucks) {
            this.activeMenu.set('gold');
        }
    }

    getIsInHomeObservable(): Observable<boolean> {
        return this.isInHome.asObservable().pipe(distinctUntilChanged());
    }

    setIsInHome(isInHome: boolean) {
        this.isInHome.next(isInHome);
    }

    async setActiveLobbyMenu(menu: LobbyMenuSelectorTypes) {
        this.activeMenu.set(menu);
    }

    setActiveSocialMenu(menu: SocialMenuSelectorTypes) {
        this.activeSocialMenu.set(menu);
    }

    getActiveLobbyMenu() {
        return this.activeMenu();
    }

    getActiveSocialMenu() {
        return this.activeSocialMenu();
    }

    async createClubDialog(popupLocation: ViewContainerName, clubListPage = false): Promise<void> {
        const dialogOptions: SopoStringInputSettings = {
            data: {
                header: 'Create Club',
                placeholder: 'Club Name',
                okText: 'Create',
                cancelText: 'Cancel',
                maxLength: 16,
            },
            sCss: clubListPage ? ['sopo-popup-red-background'] : [],
        }
        const name = await this.sopoPopupService.promptForString(popupLocation, dialogOptions);
        if (name === null || name === undefined) return;

        if (name.trim() === '') {
            this.showAlertWithTitle(popupLocation, 'Invalid club name', 'Club name cannot be empty', true);
            return;
        }

        if (name.length > 16) {
            this.showAlertWithTitle(popupLocation, 'Invalid club name', 'Club name should not exceed 16 character', true);
            return;
        }

        const pattern = new RegExp('^[a-zA-Z0-9 ]+$');
        if (!pattern.test(name.trim())) {
            this.showAlertWithTitle(popupLocation, 'Invalid club name', 'Club name should not contain special characters.', true);
            return;
        }

        this.clubService.createClub(name.trim())
        .catch(async (error) => {
            this.showAlertWithTitle(popupLocation, 'Club Undefined Created', error.error, true);
        });
    }

    async joinClubDialog(popupLocation: ViewContainerName, deepLinkClubId?: string, clubListPage = false): Promise<void> {
        const dialogOptions: SopoDoubleInputSettings = {
            data: {
                header: 'Join a club',
                firstPlaceholder: 'Club ID',
                secondPlaceholder: 'Referral ID (optional)',
                okText: 'Join',
                cancelText: 'Cancel',
                deepLinkClubId: deepLinkClubId,
            },
            doNotCloseOnClear: true,
            sCss: clubListPage ? ['sopo-popup-red-background'] : [],
        }
        const code = await this.sopoPopupService.promptForDoubleInput(popupLocation, dialogOptions);  
        if (this.checkInput(code)) return;
        const clubId = code.firstInput.trim();
        const referralId = code.secondInput.trim();
        try {
            const payload: IValidateClubByCodePayload = {
                clubCode: clubId,
            };  
            const result = await this.apiService.callClubFunction('validateClubByCode', payload);
            if(result.status !== 200) {
                this.showAlertWithTitle(popupLocation, 'Could not send a join request', result.message, true);
                return;
            }
            const clubData: IClubData = result.payload;
            const confirmResult = await this.sopoPopupService.promptForClubJoinConfirmation(popupLocation, {
                data: {
                    header: 'Confirm Join Request',
                    clubData: clubData,
                    okText: 'Send request',
                    cancelText: 'Cancel',
                    sCss: clubListPage ? ['sopo-popup-red-background'] : [],
                },
            });
            if (confirmResult !== true) return;
            await this.clubService.joinClub(clubId, referralId);
            this.showAlertWithTitle(popupLocation, `Join Request sent.`, `The club owner must accept you to join the club.`, true);
        } catch (error) {
            const message = typeof error?.error === 'string' ? error.error : 'There was a problem sending your join request, please try again later.'
            this.showAlertWithTitle(popupLocation, 'Could not send a join request', message, true);
        }
    }

    async joinTable(popupLocation: ViewContainerName, url: string) {
        if (url === null || url === undefined || url === undefined || url.trim() === '') return;
        const gamesList = await firstValueFrom(this.gameDispatcher.getState().pipe(
            map(gameState => gameState.gamesList))
        );
        if (gamesList.length >= 4) {
            this.sopoPopupService.showAlert( "app", {
                message: `You can't open more than 4 tables.`,
                backdropDismiss: false,
            });
            return;
        }
        const params = this.dynamicLinkService.extractUrlParameters(url);
        if (params.quickplayTableId) {
            const tableId = params.quickplayTableId;
            const tableIsAvailable = await this.navigateFromLinkService.tableIsAvailable(tableId);
            if (!tableIsAvailable) {
                this.showAlertWithTitle(popupLocation, 'Could not join to the table', 'The table is not available anymore');
                return;
            }
            const docReference = doc(this.firestore, `tables/${tableId}`) as DocumentReference<ITableState>;
            const table = await getDoc(docReference);
            if (table.data()) {
                const tableData = table.data();
                const clubId = tableData?.clubID;
                if (clubId && clubId !== "SOPO" && !tableData?.deleteTable) {
                    try {
                        const myId = await this.authDispatcher.getMyID();
                        if (tableData.seats.includes(myId) ) {
                            const docReference = doc(this.firestore, `clubs/${clubId}`) as DocumentReference<IClubState>;
                            const club = await getDoc(docReference);
                            if (club.data()) {
                                const clubData = club.data();
                                this.showAlertWithTitle(popupLocation, 'Could not join to the table', `This invitation link is not available anymore. The owner of the table moved it to ${clubData.name} club. You will be navigated to the table inside the club.`);
                                const game: GameToLoad = { componentType: 'game', gameStateId: tableId, tableId, tournamentId: '', clubId, type: TableType.RING };
                                await this.gameDispatcher.addNewGame(game, true);
                                return;
                            }
                        } else {
                           this.showAlertWithTitle(popupLocation, 'Could not join to the table', `This invitation link is not available anymore. You will be navigated to the main lobby.`);
                        }
                    } catch (err) {
                        console.error(err);
                    }
                    return;
                }
            }
            const game: GameToLoad = { componentType: 'game', gameStateId: tableId, tableId, tournamentId: '',  clubId: "SOPO", type: TableType.RING };
            await this.gameDispatcher.addNewGame(game, true);
            return;
        } 
        if ( params.quickplayTournamentId ) {
            const linkIsAvailable = await this.navigateFromLinkService.tournamentIsAvailable(params.quickplayTournamentId);
            if (!linkIsAvailable) {
                return this.showAlertWithTitle( popupLocation, 'Could not join to the tournament', 'Tournament is not available anymore.');
            }
            await this.sopoTransitionService.openTransition();
            await this.navCtrl.navigateForward(`tournament-registration/SOPO/${params.quickplayTournamentId}`);
            return;
        }
        this.showAlertWithTitle(popupLocation, 'Could not join to the table', 'The link does not contain any tableID or tournamentID.');
    }
    
    checkInput(value) {
        return value === null || value === undefined || value.firstInput === undefined || value.firstInput.trim() === '' ;
    }

    showAlertWithTitle(popupLocation: ViewContainerName, header: string, body: string, clubAlert = false) {
        const errorOptions: SopoAlertSettings = {
            header,
            message: body,
            dismissAfter: 5000,
            sCss: clubAlert ? ['sopo-popup-red-background'] : [],
        };
        this.sopoPopupService.showAlert(popupLocation ,errorOptions);
    }

    async createInviteLink() {
        if(this.sharingInProgress) return;
        this.sharingInProgress = true;
        const userId = await this.profileDispatcher.getMyID();
        const inviteLink = await this.dynamicLinkService.createInviteBonusLink(userId);
        if (inviteLink === '') {
            this.sharingInProgress = false;
            return;
        }
        try {
            await Clipboard.write({ string: `Come play poker for free with me on SocialPoker (SOPO)! ${inviteLink}`});
            const toast = await this.toastCtrl.create({
                'message': 'Invitation link copied to clipboard.',
                'position': 'bottom',
                'duration': 3000,
            });
            await toast.present();
            const missionId = this.featureService.sweepbucks ? 'social_invite' : 'social_invite_xp';
            this.apiService.missionCompleted({ userId, missionId: missionId, type: MissionTypes.MISSION });
        } catch (e) {
            if(e.message && !(e.message as string).includes('Share canceled')) {
                this.sopoPopupService.showAlert('app', { 
                    header: 'Error',
                    message: 'Your link could not be shared, please try again later.',
                });
                console.error(e);
            }
        }
        this.sharingInProgress = false;
    }

    ngOnDestroy() {
        this.routerSubscription?.unsubscribe();
    }
}
